import { useCallback, useEffect, useRef, useState } from 'react'
import { LocalReplyProvider } from '@/conversation/replies'
import type { ReplyProvider } from '@/conversation/replies'
import { currentGreeting } from '@/greeting/currentGreeting'
import { markSessionEnd, markSessionStart, rememberTopic } from '@/greeting/memory'
import { extractTopic } from '@/greeting/topic'
import { COPY } from '@/i18n/locales'
import { VariantRotator } from '@/i18n/format'
import { readCopy } from '@/i18n/useCopy'
import type { Profile } from '@/profile/types'
import { emptyProfile } from '@/profile/types'
import { useOnboarding } from '@/screens/Welcome/useOnboarding'
import type { Onboarding } from '@/screens/Welcome/useOnboarding'
import { useProfileStore } from '@/store/profileStore'
import { useSettingsStore } from '@/store/settingsStore'
import { useSessionStore } from '@/store/sessionStore'
import { exposeServedHook, modeFromLocation, voiceSession } from '@/voice/sessionController'
import { haptic } from '@/lib/haptics'

/**
 * The whole conversation, from the first tap to the last word.
 *
 * Two middles, one start. A first visit runs the onboarding flow and then
 * opens; every visit after that opens straight away. The ordering of the start
 * is the part worth reading closely:
 *
 *   1. Namu speaks. No microphone involved.
 *   2. Namu explains why it needs to hear you.
 *   3. Only then does the browser ask for the microphone.
 *
 * A permission prompt fires once and is remembered forever, so the request
 * made before someone knows what the app is for is the one most likely to be
 * refused. Speaking first costs nothing and is the clearest possible
 * explanation of what the microphone is for.
 *
 * Onboarding lives on this screen rather than on a route of its own, and that
 * is the same argument. The audio context is unlocked by the gesture that
 * starts the conversation, and a navigation between the tap and the first word
 * breaks it on iOS. A separate `/welcome` route would either have to make that
 * navigation or re-prepare the transport on arrival, which tears down the
 * microphone that was just granted and can prompt for it a second time.
 *
 * Onboarding runs on every session, whichever of the two middles follows it,
 * and that is worth stating plainly because this file used to say the
 * opposite. The first interview is the device's own work: it settles which
 * language the conversation happens in, takes consent before a single fact
 * leaves the phone, and writes each answer into a typed slot the profile owns.
 * Namu's agent cannot be asked to do any of that. It has no function calling
 * and refuses to answer in a structure, so nothing it says can be turned into
 * a name, a yes, or a place. On this backend a scripted flow is not the
 * fallback for meeting somebody. It is the only thing that can.
 */

export type ConversationPhase = 'welcome' | 'live' | 'ended'

export interface ConversationApi {
  phase: ConversationPhase
  /** Filled in live during onboarding, so the ledger can show it. */
  draft: Profile
  /** The first conversation: what to render, and how to answer it. */
  onboarding: Onboarding

  begin(): Promise<void>
  end(): Promise<void>
  toggleMute(): void
  toggleHold(): void
  retryMicrophone(): Promise<void>
}

export function useConversation(): ConversationApi {
  const [phase, setPhase] = useState<ConversationPhase>('welcome')
  const [draft, setDraft] = useState<Profile>(() => useProfileStore.getState().profile)
  const onboarding = useOnboarding()

  const providerRef = useRef<ReplyProvider>(new LocalReplyProvider())
  const rotatorRef = useRef(new VariantRotator())
  const abortRef = useRef<AbortController | null>(null)
  const runningRef = useRef(false)

  // Tear the session down if the screen unmounts mid-conversation, so the
  // microphone light does not stay on after the user has navigated away.
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      if (voiceSession.isPrepared) void voiceSession.teardown()
    }
  }, [])

  // The OS suspends the audio context when the app goes to the background, and
  // it does not come back on its own.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void voiceSession.resumeAudio()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  const speak = useCallback(async (text: string) => {
    if (text.length === 0) return
    await voiceSession.speak(text)
  }, [])

  const begin = useCallback(async () => {
    if (runningRef.current) return
    runningRef.current = true

    const settings = useSettingsStore.getState()
    const controller = new AbortController()
    abortRef.current = controller

    setPhase('live')
    haptic('tap')

    /*
     * Chosen before anything is written, and this ordering is the whole
     * contract with the greeting memory. The band is the distance from the
     * last conversation to this one, so stamping the clock first would measure
     * this session against itself and answer everybody, forever, with "Yes?".
     */
    const greeting = currentGreeting((key, variants) => rotatorRef.current.next(key, variants))
    markSessionStart()

    await voiceSession.prepare({
      mode: modeFromLocation(),
      locale: settings.locale,
      rate: settings.rate,
      persona: settings.persona,
    })

    /*
     * The same moment `watchAnswers` reports on a real session, for the one
     * conversation where nothing can report it.
     *
     * The open loop below only ever speaks lines this app wrote, and the
     * on-device provider never returns an `answer` intent, so a mock session
     * has no answer to observe and the place question would sit forever behind
     * a condition that cannot come true in a browser.
     *
     * A flag rather than a direct call, and that is the whole point of it.
     * Raising the event from outside the loop leaves the loop listening while
     * onboarding asks its question, so two turns compete for one microphone
     * and the answer to "where are you" is taken by whichever asked last. The
     * flag is read exactly where the intent is, so a simulated answer is
     * handled at the same moment a real one would be.
     */
    let modelAnswered = false
    exposeServedHook(() => {
      modelAnswered = true
    })

    // Read fresh at every use, never captured. The conversation can switch
    // language partway through, and a captured copy object meant the session
    // went on speaking the language the person had just asked to leave.
    const copy = () => readCopy()

    try {
      await runConversation(controller.signal)
    } finally {
      runningRef.current = false
      /*
       * Stamped again on the way out, and on the aborted path too. A half hour
       * of conversation measured from its start put somebody who came back
       * five minutes later into the "hello again" band, because the clock was
       * counting from a point they had never left.
       */
      markSessionEnd()
      if (!controller.signal.aborted) {
        setPhase('ended')
        await speak(rotatorRef.current.next('goodbye', copy().speech.goodbye))
        // Tearing down is not optional here. A conversation that runs itself
        // out, because the person simply stopped answering, has to release the
        // microphone and move the machine to `ended` exactly as pressing End
        // does. Without it the recording indicator stays lit on the phone and
        // the status keeps claiming to be listening to someone who has left.
        await voiceSession.teardown()
      }
    }

    async function ensureMicrophone(signal: AbortSignal): Promise<boolean> {
      const existing = await voiceSession.permission()
      if (existing !== 'granted') {
        // The primer is spoken, not shown as a blocking dialog that mimics the
        // system alert. Guidance is explicit that a screen imitating the OS
        // prompt, with an "Allow" button of its own, is the wrong pattern.
        await speak(rotatorRef.current.next('micPrimer', copy().speech.micPrimer))
      }
      if (signal.aborted) return false

      const granted = await voiceSession.openMicrophone()
      if (!granted) {
        // There is no typed path any more, so a refused microphone is a dead
        // end until it is granted. The error banner says what happened and
        // offers the retry, which is the fresh gesture a browser needs before
        // it will prompt again.
        await speak(rotatorRef.current.next('micDenied', copy().speech.micDenied))
      }
      return granted
    }

    /**
     * The interview first, then one of two very different middles.
     *
     * The interview is the same on both paths, because it is the app's own: it
     * asks four questions, parses the answers into typed slots and writes them.
     * Nothing about that changes because the words come back over a socket.
     *
     * What the transport does change is who runs the open conversation
     * afterwards. With Namu's own model the assistant is a participant: it
     * listens continuously, it decides when a turn ended and it can be talked
     * over. There is nothing for this hook to compose, so it gets out of the
     * way. With the browser's speech stack, or the mock, the app is the author:
     * it speaks a line, opens the microphone for exactly one turn, works out a
     * reply and speaks that. Wrapping that loop around a model that is already
     * talking would produce an assistant talking over itself, which is why this
     * branches rather than pretending the two are the same shape.
     */
    async function runConversation(signal: AbortSignal): Promise<void> {
      if (onboarding.runner.due) {
        await runOnboarding(signal)
        if (signal.aborted) return

        if (voiceSession.isDuplex) {
          await runDuplex(signal)
          return
        }
        /*
         * Never `greet: true` here. The flow's last act is the invitation, and
         * the invitation carries the name: that sentence is the moment the
         * product proves it was listening, and following it with "I am
         * listening" spends it on a line that could have been said to anybody.
         */
        await openLoop(signal, { greet: false })
        return
      }

      if (voiceSession.isDuplex) {
        // No line of ours, because the opening comes from the model the moment
        // the session attaches. Speaking here would be the second voice.
        await runDuplex(signal)
        return
      }

      /*
       * The greeting was chosen up in `begin` and is spoken, not printed. A
       * returning person is welcomed back by voice, which is the entire point
       * of the product, and it happens before the microphone is asked for so
       * that the permission prompt arrives after Namu has spoken.
       *
       * Whether the loop then opens with "I am listening" is the greeting's
       * decision rather than a constant, because "Yes, Amina?" has already
       * asked and "Good morning, Amina." has not.
       */
      await speak(greeting.text)
      if (signal.aborted) return

      await ensureMicrophone(signal)
      if (signal.aborted) return

      await openLoop(signal, { greet: greeting.invite })
    }

    /** Everything up to the invitation. Resolves when Namu takes the floor. */
    async function runOnboarding(signal: AbortSignal): Promise<void> {
      /*
       * Two openings for the same moment, and exactly one of them is spoken.
       *
       * `selectGreeting` and the onboarding flow both have a line for somebody
       * arriving, and they are for different people. A stranger gets the
       * bilingual opening, because the language of their reply is what
       * settles which language this conversation happens in, and there is no
       * name to greet them by. Everybody else gets the greeting that knows what
       * day it is, including somebody who abandoned onboarding last week and is
       * about to be asked the question they stopped on. Letting both speak
       * would open a first visit with two introductions in a row.
       */
      if (!onboarding.runner.opensTheConversation) {
        await speak(greeting.text)
        if (signal.aborted) return
      }

      // The microphone is asked for from inside the flow, at the first turn
      // that needs one, so the prompt still arrives after Namu has spoken.
      await onboarding.runner.run({
        signal,
        ensureMicrophone: () => ensureMicrophone(signal),
      })
    }

    /**
     * The model drives. This waits, and listens for one thing.
     *
     * The microphone has to come first: nothing can be heard until it is open.
     * After that there is nothing to compose. The transport is streaming, the
     * model is talking, and every state change arrives as an event the store
     * has already turned into what the screen shows.
     *
     * The one thing still owed to onboarding is the place question, and this is
     * where it is earned. The app did not write the model's reply and cannot
     * read an intent off it, so `watchAnswers` reports the shape instead: the
     * person spoke, and the assistant answered them to the end. That is the
     * moment "where are you? what I can tell you about a clinic is different in
     * Kano and in Sokoto" is a question about the answer rather than a stranger
     * asking where somebody lives.
     */
    async function runDuplex(signal: AbortSignal): Promise<void> {
      const granted = await ensureMicrophone(signal)
      if (signal.aborted || !granted) return

      // Guarded against re-entry rather than unsubscribed on the first answer.
      // `served` speaks and then waits for a reply, and a second answer landing
      // during that wait would start the same question again underneath the
      // first. The flow settles itself once, so later answers cost nothing.
      let asking = false
      const stopWatching = voiceSession.watchAnswers(() => {
        if (asking) return
        asking = true
        void onboarding.runner.served(signal).finally(() => {
          asking = false
        })
      })

      try {
        // Resolves when the session ends, which is either the person pressing
        // End or the connection giving up.
        await voiceSession.whenEnded(signal)
      } finally {
        stopWatching()
      }
    }

    /** The open exchange: listen, reply, repeat, until the person is done. */
    async function openLoop(signal: AbortSignal, options: { greet: boolean }): Promise<void> {
      if (options.greet) {
        await speak(rotatorRef.current.next('listeningPrompt', copy().speech.listeningPrompt))
      }

      let consecutiveSilences = 0

      while (!signal.aborted) {
        // Waiting out a hold is not the person going quiet.
        await voiceSession.whenResumed()
        if (signal.aborted) return

        const result = await voiceSession.listen({
          ...useSettingsStore.getState().timings(),
          signal,
        })

        if (signal.aborted || result.status === 'aborted') return
        // The turn was cut short by Hold, not by the person. Go round again
        // once they resume rather than counting it as silence.
        if (voiceSession.isHeld) continue

        if (result.status === 'timeout') {
          consecutiveSilences += 1
          // One check-in, then leave the person alone rather than nagging.
          if (consecutiveSilences === 1) {
            await speak(rotatorRef.current.next('stillThere', copy().speech.stillThere))
            continue
          }
          return
        }

        if (result.status === 'unintelligible') {
          await speak(rotatorRef.current.next('didNotCatch', copy().speech.didNotCatch))
          continue
        }

        consecutiveSilences = 0
        haptic('turn')

        /*
         * Anything offered rather than asked for.
         *
         * Age band and gender are deliberately not onboarding questions, and
         * this is the only way either of them is ever recorded. It is not the
         * extractors run over open conversation: `volunteered.ts` accepts a
         * fact only from a first-person frame, because "my brother is a farmer,
         * he is in Kano" otherwise records the speaker as a man.
         */
        onboarding.runner.overhear(result.transcript, useSettingsStore.getState().locale)

        const reply = await providerRef.current.respond({
          text: result.transcript,
          profile: useProfileStore.getState().profile,
          locale: useSettingsStore.getState().locale,
          copy: copy(),
        })

        if (reply.patch?.name) {
          useProfileStore.getState().setName(reply.patch.name, 'confirmed')
          setDraft({ ...useProfileStore.getState().profile })
        }

        if (reply.patch?.locale) {
          const next = reply.patch.locale
          useSettingsStore.getState().chooseLocale(next)
          useProfileStore.getState().setLanguage(next)
          voiceSession.setLocale(next)
          // Confirmed in the language just switched to, because that is the
          // only language the confirmation is any use in.
          await speak(COPY[next].speech.languageSwitched[0] as string)
          continue
        }

        /*
         * What this conversation was about, kept for the next greeting.
         *
         * Only the intents that are somebody asking Namu something. A rename,
         * a language switch, a goodbye and a request for the profile are the
         * person operating the app rather than talking to it, and "last time
         * we spoke about call me Amina" is the sort of line that makes a
         * product sound like it was not listening at all.
         *
         * The consent answer is read on every turn rather than captured once,
         * so revoking it takes effect within the conversation it is revoked
         * in, which is also where the stored topic gets cleared.
         */
        if (reply.intent === 'answer' || reply.intent === 'unsupported') {
          rememberTopic(
            extractTopic(result.transcript, useSettingsStore.getState().locale),
            useProfileStore.getState().hasConsent(),
          )
        }

        if (reply.intent === 'end') {
          await speak(reply.text)
          return
        }

        await speak(reply.text)

        /*
         * The place question, earned the way it can be earned here.
         *
         * On this path the app wrote the reply, so it can read the intent
         * straight off it, and only an `answer` counts. The duplex path has no
         * intent to read and watches the shape of the exchange instead; the two
         * are the same rule measured where the information actually is.
         *
         * The on-device provider never returns that intent, and that is the
         * correct outcome rather than a gap: asking somebody where they live
         * immediately after "my language model is not connected yet" would be
         * absurd. It is also why the mock session needs the flag above.
         */
        const answered = reply.intent === 'answer' || modelAnswered
        modelAnswered = false
        if (answered) await onboarding.runner.served(signal)
      }
    }
  }, [speak, onboarding.runner])

  const end = useCallback(async () => {
    abortRef.current?.abort()
    voiceSession.cancelSpeech()
    await voiceSession.teardown()
    setPhase('ended')
    runningRef.current = false
    haptic('tap')
  }, [])


  const toggleMute = useCallback(() => {
    const next = !useSessionStore.getState().muted
    voiceSession.setMuted(next)
    haptic('tap')
  }, [])

  const toggleHold = useCallback(() => {
    const next = !useSessionStore.getState().held
    voiceSession.setHeld(next)
    haptic('tap')
  }, [])

  const retryMicrophone = useCallback(async () => {
    const granted = await voiceSession.openMicrophone()
    if (granted) useSessionStore.getState().clearError()
  }, [])

  return {
    phase,
    draft,
    onboarding,
    begin,
    end,
    toggleMute,
    toggleHold,
    retryMicrophone,
  }
}


/** Used by the profile screen's fresh-start action. */
export function resetDraft(): Profile {
  return emptyProfile()
}
